import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Grid, GridCol } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';
import styled from 'styled-components';
import { spacing, breakpoints, themeLight, radius, fontStyle } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { AnimateText, AnimateStatic } from '@naf/animation';
import { propValidationConfig } from './propValidationConfig';
export const FeatureHeader = /*#__PURE__*/forwardRef(({
  title,
  subText,
  runningHead,
  imageOrVideo,
  nafBranded = true,
  className,
  overlay,
  caption,
  clickThroughHeader
}, ref) => {
  const [subTextHeightValue, setSubTextHeightValue] = useState('0');
  const [mediaWidthValue, setMediaWidthValue] = useState('');
  const [videoHeightValue, setVideoHeightValue] = useState('');
  const [captionHeightValue, setCaptionHeightValue] = useState('');
  const ingressRef = useRef(null);
  const mediaRef = useRef(null);
  const captionRef = useRef(null);
  useEffect(() => {
    if (subText && ingressRef && ingressRef.current && typeof window !== 'undefined') {
      const ingressHeight = ingressRef.current.clientHeight;
      if (ingressHeight) setSubTextHeightValue(`${ingressHeight}`);
    }
  }, [subText]);
  useEffect(() => {
    if (caption && captionRef && captionRef.current && typeof window !== 'undefined') {
      const captionHeight = captionRef.current.clientHeight;
      if (captionHeight) setCaptionHeightValue(`${captionHeight}`);
    }
  }, [caption]);
  useEffect(() => {
    const currentMediaRef = mediaRef.current;
    function handleResize() {
      if (imageOrVideo && currentMediaRef && typeof window !== 'undefined') {
        const mediaWidth = currentMediaRef.clientWidth;
        let video;
        if (currentMediaRef.getElementsByTagName('video') && currentMediaRef.getElementsByTagName('video')?.length > 0) {
          video = currentMediaRef.getElementsByTagName('video');
        } else if (currentMediaRef.getElementsByTagName('iframe') && currentMediaRef.getElementsByTagName('iframe')?.length > 0) {
          video = currentMediaRef.getElementsByTagName('iframe');
        }
        const videoHeight = video && video?.length > 0 && video[0] && video[0].clientHeight;
        if (mediaWidth) setMediaWidthValue(`${mediaWidth}`);
        if (videoHeight) setVideoHeightValue(`${videoHeight}`);
      }
    }
    window.addEventListener('resize', handleResize);
    if (imageOrVideo && currentMediaRef) {
      let video;
      if (currentMediaRef.getElementsByTagName('video') && currentMediaRef.getElementsByTagName('video')?.length > 0) {
        video = currentMediaRef.getElementsByTagName('video');
      } else if (currentMediaRef.getElementsByTagName('iframe') && currentMediaRef.getElementsByTagName('iframe')?.length > 0) {
        video = currentMediaRef.getElementsByTagName('iframe');
      }
      if (video && video?.length > 0) {
        video[0].addEventListener('loadedmetadata', handleResize);
      }
    }
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      if (imageOrVideo && currentMediaRef) {
        let video;
        if (currentMediaRef.getElementsByTagName('video') && currentMediaRef.getElementsByTagName('video')?.length > 0) {
          video = currentMediaRef.getElementsByTagName('video');
        } else if (currentMediaRef.getElementsByTagName('iframe') && currentMediaRef.getElementsByTagName('iframe')?.length > 0) {
          video = currentMediaRef.getElementsByTagName('iframe');
        }
        if (video && video?.length > 0) {
          video[0].removeEventListener('loadedmetadata', handleResize);
        }
      }
    };
  }, [imageOrVideo, overlay, mediaRef]);
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: className,
    ref: ref,
    subTextHeight: subTextHeightValue,
    mediaWidth: mediaWidthValue,
    videoHeight: videoHeightValue
  }, /*#__PURE__*/React.createElement(ImageWrapper, {
    mediaWidth: mediaWidthValue,
    videoHeight: videoHeightValue
  }, imageOrVideo && /*#__PURE__*/React.createElement(Image, {
    mediaWidth: mediaWidthValue,
    videoHeight: videoHeightValue,
    ref: mediaRef,
    overlay: overlay
  }, imageOrVideo)), /*#__PURE__*/React.createElement(Header, {
    mediaWidth: mediaWidthValue,
    videoHeight: videoHeightValue,
    $clickThroughHeader: clickThroughHeader
  }, /*#__PURE__*/React.createElement(StyledGridCol, {
    s: "12",
    m: "12",
    l: "12",
    xl: "12",
    captionHeight: captionHeightValue
  }, runningHead && /*#__PURE__*/React.createElement(StyledRunningHead, {
    tag: "p",
    variant: TextVariant.Tag
  }, runningHead), /*#__PURE__*/React.createElement(AnimateText, {
    textProps: {
      variant: TextVariant.Header1
    }
  }, title), nafBranded && /*#__PURE__*/React.createElement(AnimateStatic, {
    delay: 600,
    timeout: 1000
  }, /*#__PURE__*/React.createElement(YellowLine, null))), subText && /*#__PURE__*/React.createElement(Ingress, {
    mediaWidth: mediaWidthValue,
    videoHeight: videoHeightValue,
    ref: ingressRef,
    variant: TextVariant.Ingress
  }, subText), caption && /*#__PURE__*/React.createElement(Caption, {
    ref: captionRef
  }, caption)));
});
export const Caption = styled.span.withConfig({
  displayName: "FeatureHeader__Caption",
  componentId: "sc-1awjrb4-0"
})(["position:absolute;color:", ";", ";background-color:rgba(26,26,26,0.7);bottom:", ";margin:0 ", " 0 0;padding:0 ", ";border-radius:", ";z-index:3;grid-column:9 / 13;justify-self:end;@media (max-width:", "){grid-column:1 / -1;margin:0 ", ";}"], ({
  theme
}) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, fontStyle.article.caption, spacing.space20, spacing.space20, spacing.space8, radius.s, breakpoints.s, spacing.space12);
const StyledRunningHead = styled(Text).withConfig({
  displayName: "FeatureHeader__StyledRunningHead",
  componentId: "sc-1awjrb4-1"
})(["text-shadow:0 2px 6px rgba(0,0,0,0.4);"]);
const Header = styled(Grid).withConfig({
  displayName: "FeatureHeader__Header",
  componentId: "sc-1awjrb4-2"
})(["grid-gap:", " !important;margin:auto;position:relative;align-items:flex-end;", " h1{margin-top:0;text-shadow:0 2px 6px rgba(0,0,0,0.4);}", " ", ";@media (max-width:", "){min-height:80vh;", "}"], spacing.space24, ({
  $clickThroughHeader
}) => $clickThroughHeader ? 'pointer-events: none;' : '', props => props.mediaWidth ? `min-height: calc(0.664 * ${props.mediaWidth}px);` : 'min-height: 80vh;', props => props.videoHeight && `min-height: ${props.videoHeight}px;`, breakpoints.s, props => props.videoHeight && `min-height: ${props.videoHeight}px;`);
const ImageWrapper = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "FeatureHeader__ImageWrapper",
  componentId: "sc-1awjrb4-3"
})(["position:absolute;top:0;width:100%;z-index:-50;", " ", ";@media (max-width:", "){height:80vh;", "}"], props => props.mediaWidth ? `height: calc(0.664 * ${props.mediaWidth}px);` : 'height: 80vh;', props => props.videoHeight && `height: ${props.videoHeight}px;`, breakpoints.s, props => props.videoHeight && `height: ${props.videoHeight}px;`);
const StyledGridCol = styled(GridCol).withConfig({
  displayName: "FeatureHeader__StyledGridCol",
  componentId: "sc-1awjrb4-4"
})(["color:#fff;grid-column:2 / 9;margin-bottom:", ";padding-bottom:", ";@media (max-width:", "){padding:0 ", ";grid-column:1 / -1;", "}"], spacing.space32, spacing.space64, breakpoints.s, spacing.space24, props => props.captionHeight ? `margin-bottom: calc(${spacing.space48} + ${props.captionHeight}px);` : `margin-bottom: ${spacing.space32};`);
const Ingress = styled(Text).withConfig({
  displayName: "FeatureHeader__Ingress",
  componentId: "sc-1awjrb4-5"
})(["grid-column:2 / 9;background-color:white;padding:", " ", ";height:fit-content;align-self:flex-end;position:absolute;left:-", ";", " ", ";@media (max-width:", "){grid-column:2 / 11;}@media (max-width:", "){grid-column:1 / -1;padding:", " ", ";margin:", " 0;left:0;top:80vh;", "}"], spacing.space24, spacing.space32, spacing.space32, props => props.mediaWidth ? `top: calc(0.664 * ${props.mediaWidth}px - 77px);` : 'top: calc(80vh - 77px);', props => props.videoHeight && `top: calc(${props.videoHeight}px - 77px);`, breakpoints.l, breakpoints.s, spacing.space16, spacing.space24, spacing.space8, props => props.videoHeight && `top: ${props.videoHeight}px;`);
const YellowLine = styled.div.withConfig({
  displayName: "FeatureHeader__YellowLine",
  componentId: "sc-1awjrb4-6"
})(["background-color:", ";width:94px;height:4px;"], nafColor.signature.yellow);
const Image = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "FeatureHeader__Image",
  componentId: "sc-1awjrb4-7"
})(["& > img,& > video{width:100%;object-fit:cover;z-index:1;display:block;}", " ", " ", " img{", "}@media (max-width:", "){height:80vh;", " img{height:80vh;}}"], ({
  overlay
}) => overlay && `
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: 
        radial-gradient(140.28% 84.01% at 54.18% 20.35%, rgba(0, 0, 0, 0.00) 49%, rgba(0, 0, 0, 0.08) 64%, rgba(0, 0, 0, 0.61) 100%),
        radial-gradient(218.33% 124.37% at 64.41% 0%, rgba(0, 0, 0, 0.00) 47%, rgba(0, 0, 0, 0.48) 100%);
      z-index: 50;
    }
  `, props => props.mediaWidth ? `height: calc(0.664 * ${props.mediaWidth}px);` : 'height: 80vh;', props => props.videoHeight && `height: ${props.videoHeight}px;`, props => props.mediaWidth ? `height: calc(0.664 * ${props.mediaWidth}px);` : 'height: 80vh;', breakpoints.s, props => props.videoHeight && `height: ${props.videoHeight}px;`);
const Wrapper = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "FeatureHeader__Wrapper",
  componentId: "sc-1awjrb4-8"
})(["position:relative;width:100%;margin-bottom:", ";z-index:5;", " ", " ", ";@media (max-width:", "){", " ", "}"], spacing.space48, props => props.subTextHeight ? `height: calc(80vh + ${props.subTextHeight}px - 77px);` : 'height: 80vh;', props => props.subTextHeight && props.mediaWidth && `height: calc((0.664 * ${props.mediaWidth}px) + ${props.subTextHeight}px - 77px);`, props => props.videoHeight && props.subTextHeight && `height: calc(${props.videoHeight}px + ${props.subTextHeight}px - 77px);`, breakpoints.s, props => props.subTextHeight ? `height: calc(80vh + ${props.subTextHeight}px);` : 'height: 80vh;', props => props.videoHeight && props.subTextHeight && `height: calc(${props.videoHeight}px + ${props.subTextHeight}px);`);
//# sourceMappingURL=FeatureHeader.js.map